import type { MetricPolarity } from "@hightouch/lib/ad-studio/insights-malloy/schemas/cube-meta";
import type {
  MetricCell,
  MetricKey,
  Reported,
} from "src/pages/ad-studio/insights/performance/types";

export type MetricDistribution = {
  average: number;
  min: number;
  max: number;
};

const NEUTRAL_COLOR = { bg: "gray.100", color: "text.primary" };
const GREEN_RAMP = ["green.100", "green.200", "green.300"] as const;

export type MetricDistributions<Key extends string = MetricKey> =
  | { kind: "average-pending" }
  | { kind: "distributions"; byMetric: ReadonlyMap<Key, MetricDistribution> };

const trimmedOf = (sorted: readonly number[]): readonly number[] => {
  const trimCount = Math.floor(sorted.length * 0.1);
  return trimCount > 0
    ? sorted.slice(trimCount, sorted.length - trimCount)
    : sorted;
};

export type Averages<Key extends string = MetricKey> =
  | { kind: "aggregate-pending" }
  | { kind: "no-aggregate" }
  | { kind: "aggregate"; values: ReadonlyMap<Key, number> };

const toDistribution = (
  values: readonly [number, ...number[]],
  average: number,
): MetricDistribution => {
  const trimmed = trimmedOf([...values].sort((left, right) => left - right));
  return {
    average,
    min: Math.min(...trimmed),
    max: Math.max(...trimmed),
  };
};

const distributionsOf = <Key extends string>({
  rows,
  metrics,
  averages,
}: {
  rows: readonly { metrics: readonly MetricCell[] }[];
  metrics: readonly Key[];
  averages: ReadonlyMap<Key, number>;
}): ReadonlyMap<Key, MetricDistribution> => {
  const cells = rows.flatMap((row) => row.metrics);
  return new Map(
    metrics.flatMap((metric) => {
      const [first, ...rest] = cells.flatMap(({ column, value }) =>
        column.key === metric && typeof value === "number" && value !== 0
          ? [value]
          : [],
      );
      const average = averages.get(metric);
      return first === undefined || average === undefined
        ? []
        : [[metric, toDistribution([first, ...rest], average)] as const];
    }),
  );
};

export function buildMetricDistributions<Key extends string>({
  rows,
  metrics,
  averages,
}: {
  rows: readonly { metrics: readonly MetricCell[] }[];
  metrics: readonly Key[];
  averages: Averages<Key>;
}): MetricDistributions<Key> {
  switch (averages.kind) {
    case "aggregate-pending": {
      return { kind: "average-pending" };
    }
    case "no-aggregate": {
      return { kind: "distributions", byMetric: new Map() };
    }
    case "aggregate": {
      return {
        kind: "distributions",
        byMetric: distributionsOf({ rows, metrics, averages: averages.values }),
      };
    }
    default: {
      return averages satisfies never;
    }
  }
}

export function getMetricDistributionColor({
  polarity,
  value,
  distribution,
}: {
  polarity: MetricPolarity;
  value: Reported;
  distribution: MetricDistribution | "no-distribution";
}): { bg: string; color: string } {
  if (
    value === "not-reported" ||
    value === 0 ||
    distribution === "no-distribution" ||
    distribution.min === distribution.max
  ) {
    return { bg: "base.background", color: "text.secondary" };
  }

  const higherIsBetter = polarity !== "lower";
  const isAboveAveragePerformance = higherIsBetter
    ? value > distribution.average
    : value < distribution.average;

  if (!isAboveAveragePerformance) {
    return NEUTRAL_COLOR;
  }

  const scaleEnd = higherIsBetter ? distribution.max : distribution.min;
  const scaleDistance = higherIsBetter
    ? scaleEnd - distribution.average
    : distribution.average - scaleEnd;
  const score =
    scaleDistance <= 0
      ? 1
      : clamp(
          higherIsBetter
            ? (value - distribution.average) / scaleDistance
            : (distribution.average - value) / scaleDistance,
        );
  const shadeIndex = Math.min(
    GREEN_RAMP.length - 1,
    Math.floor(score * GREEN_RAMP.length),
  );
  const bg = GREEN_RAMP[shadeIndex] ?? "green.300";

  return {
    bg,
    color: "green.800",
  };
}

function clamp(value: number): number {
  return Math.min(1, Math.max(0, value));
}
